import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';

const PILLARS = [
  {
    id: 'structure',
    icon: 'ri-line-chart-line',
    title: 'Market Structure',
    text: 'Reads break-of-structure and swing highs and lows on XAUUSD M15 to define the day\u2019s directional bias before any entry is considered.',
  },
  {
    id: 'momentum',
    icon: 'ri-speed-up-line',
    title: 'Momentum Confirmation',
    text: 'Waits for price to break market structure, then enters on the controlled pullback that follows — riding momentum instead of chasing the breakout.',
  },
  {
    id: 'volatility',
    icon: 'ri-flashlight-line',
    title: 'Volatility Filter',
    text: 'An ATR-based filter skips dead or erratic conditions where gold spreads widen and stop hunts become most likely.',
  },
  {
    id: 'news',
    icon: 'ri-alarm-warning-line',
    title: 'News & Spread Guard',
    text: 'Avoids new entries around high-impact US releases and steps aside when spreads spike, protecting entries from slippage.',
  },
];

export default function StrategySection() {
  return (
    <section id="strategy" className="py-14 md:py-20 bg-background-50 scroll-mt-32 md:scroll-mt-36">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              align="left"
              eyebrow="Strategy"
              title="A structure-first approach built only for gold"
              subtitle="Gold trends violently but shakes out over-leveraged traders constantly. GoldWarden focuses on entering with the trend at controlled risk, then lets the market do the work."
            />
          </Reveal>

          <Reveal delay={60}>
            <p className="mt-6 max-w-3xl border-l-2 border-accent-400 pl-5 text-sm md:text-base leading-relaxed text-foreground-700">
              <strong className="font-semibold text-foreground-950">The core idea:</strong> gold trends
              hardest while London and New York are open. So GoldWarden waits for price to break market
              structure, then enters on the controlled pullback that follows — always with the trend,
              always sized small, so no single trade can ever define the account.
            </p>
          </Reveal>

          <div className="mt-10 md:mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <Reveal className="lg:col-span-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {PILLARS.map((pillar) => (
                  <div
                    key={pillar.id}
                    className="h-full rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 hover:border-accent-300 transition-colors duration-200"
                  >
                    <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-950 text-accent-400">
                      <i className={`${pillar.icon} text-xl`} aria-hidden="true"></i>
                    </span>
                    <h3 className="mt-4 font-heading text-sm md:text-base font-semibold text-foreground-950">
                      {pillar.title}
                    </h3>
                    <p className="mt-2 text-xs md:text-sm leading-relaxed text-foreground-600">{pillar.text}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={140}>
              <div className="h-full rounded-lg border border-background-200 bg-background-100 p-5 md:p-7 flex flex-col">
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                  What it never does
                </h3>
                <ul className="mt-5 flex flex-col gap-3.5">
                  {[
                    'Never uses martingale or doubles losers',
                    'Never grids or averages down a position',
                    'Never trades without a hard stop-loss',
                    'Never risks more than a set equity fraction',
                    'Never holds trades through major news blindly',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-xs md:text-sm text-foreground-700">
                      <i className="ri-close-circle-line text-secondary-500 mt-0.5" aria-hidden="true"></i>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 rounded-lg bg-primary-950 p-4">
                  <p className="text-xs md:text-sm leading-relaxed text-background-200/90">
                    Survivability comes first. A strategy that risks the account to chase a big month is
                    not a strategy — it is a countdown.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}